import { Fragment, useCallback, useEffect } from 'react';
import tw from 'twin.macro';
import { Trans, useTranslation } from 'react-i18next';

import { useAuthContext } from '@auth_module/context/AuthContext';
import { CloseIcon } from '@icons/CloseIcon';
import { SounderLogoWithTitle } from '@icons/SounderLogoWithTitle';
import { Spacer } from '@components/Spacer';

const Overlay = tw.div`absolute top-0 left-0 bottom-0 right-0 flex justify-center bg-neutral-background z-30`;
const OuterWrapper = tw.div`relative w-[800px] mt-[60px] xl:mt-[120px] px-6 text-sm font-medium`;
const CloseButton = tw.div`absolute right-0 top-[-44px] w-8 h-8 flex justify-center items-center cursor-pointer`;
const Logo = tw.div`mb-10`;
const Title = tw.div`text-[32px] leading-[51px]`;
const InnerWrapper = tw.div`h-[calc(100% - 210px)]`;
const ScrollableWrapper = tw.div`overflow-y-auto h-full pr-4`;
const StyledA = tw.a`text-text-and-icon underline`;
const SegmentTitle = tw.div`text-base font-bold text-text-and-icon`;
const SegmentContent = tw.div`text-sm font-medium text-text-and-icon-muted`;

const TermsSegment = ({ title, content }: { title: string; content: string }) => {
  return (
    <>
      <SegmentTitle>{title.toUpperCase()}</SegmentTitle>
      <Spacer direction='vertical' size='xs' />
      <SegmentContent>{content}</SegmentContent>
    </>
  );
};

export const PromotionHubTermsOfServiceModal = () => {
  const { t, ready } = useTranslation();
  const { showTermsOfService, setShowTermsOfService } = useAuthContext();

  const sections = t('auth.promotion_hub_terms_of_service_page.segments', {
    returnObjects: true,
  }) as {
    title: string;
    content: string;
  }[];

  const handlePopState = useCallback(
    (e: Event) => {
      if (showTermsOfService) {
        e.preventDefault();
        setShowTermsOfService(false);
        history.replaceState(null, '', location.href);
      } else {
        history.back();
      }
    },
    [showTermsOfService]
  );

  useEffect(() => {
    window.addEventListener('popstate', handlePopState);
    history.pushState(null, '', location.href);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [handlePopState]);

  if (!showTermsOfService || !ready) return null;

  return (
    <Overlay>
      <OuterWrapper>
        <CloseButton onClick={() => setShowTermsOfService(false)}>
          <CloseIcon tw=' fill-primary w-4 h-4' />
        </CloseButton>

        <Logo>
          <SounderLogoWithTitle tw='fill-secondary' />
        </Logo>

        <Title>{t('auth.promotion_hub_terms_of_service_page.title')}</Title>

        <Spacer direction='vertical' size='xl' />

        <InnerWrapper>
          <ScrollableWrapper>
            <SegmentContent>
              <Trans i18nKey='auth.promotion_hub_terms_of_service_page.intro_p_1'>
                Sounder terms
                <StyledA href='https://www.sounder.ai/en/privacy-policy' target='_blank'>
                  privacy policy
                </StyledA>
              </Trans>
            </SegmentContent>

            <Spacer direction='vertical' size='xl' />

            <SegmentContent>
              {t('auth.promotion_hub_terms_of_service_page.intro_p_2')}
            </SegmentContent>

            <Spacer direction='vertical' size='xl' />

            {sections.map((segment, i) => (
              <Fragment key={i}>
                <TermsSegment title={segment.title} content={segment.content} />
                <Spacer direction='vertical' size='xl' />
              </Fragment>
            ))}
          </ScrollableWrapper>
        </InnerWrapper>
      </OuterWrapper>
    </Overlay>
  );
};
